import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export const TABS = {
  APP_REVIEW: 0,
  REDISCOVERY: 1,
} as const;

export type ActiveTab = typeof TABS[keyof typeof TABS];

const VALID_TABS = new Set<number>(Object.values(TABS));

/**
 * This context is used for shared data that is relevant to all tabs in talent matching.
 * Tab specific data should be added to the tab's context.
 */
export interface UserContextValue {
  activeTab: ActiveTab;
  setActiveTab: (tab: number) => void;
  calibrationTerms: string[];
  canCreateTags: boolean;
  canExport: boolean;
  canUseLocations: boolean;
  isSiteAdmin: boolean;
  userDateFormat: string;
  linkedinRscEnabled: boolean;
  canUseSecurityConcerns: boolean;
  userId: number;
  hiringPlanId: number;
}

export const UserContext = createContext<UserContextValue | undefined>(
  undefined
);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext not used within a UserContext.Provider');
  }
  return context;
};

export type UserContextProviderProps = {
  value: Omit<UserContextValue, 'activeTab' | 'setActiveTab'>;
  children: React.ReactNode;
};

export const UserContextProvider = ({
  value,
  children,
}: UserContextProviderProps) => {
  const [activeTab, setActiveTabState] = useState<ActiveTab>(TABS.APP_REVIEW);

  const setActiveTab = useCallback((tab: number) => {
    if (VALID_TABS.has(tab)) {
      setActiveTabState(tab as ActiveTab);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      ...value,
      activeTab,
      setActiveTab,
    }),
    [value, activeTab, setActiveTab]
  );

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};
